import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlignRight, ChevronDown, User, Package2, LogOut, Globe } from 'lucide-react';
import MobileMenu from './MobileMenu';
import SocialLinks from '../SocialLinks';
import RequestForm from '../services/RequestForm';
import { supabase } from '../../lib/supabaseClient';
import { useLanguage } from '../../contexts/LanguageContext';

function NavigationMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isPricesOpen, setIsPricesOpen] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    // Check authentication status on mount and when auth state changes
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const isAuthenticatedLocal = localStorage.getItem('isAuthenticated') === 'true';
      setIsAuthenticated(!!session || isAuthenticatedLocal);
    };

    checkAuth();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session || localStorage.getItem('isAuthenticated') === 'true');
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleProfileClick = () => {
    if (isAuthenticated) {
      navigate('/client/dashboard');
    } else {
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    // Clear Supabase session
    await supabase.auth.signOut();
    
    // Clear local storage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('userId');
    localStorage.removeItem('userFirstName');
    localStorage.removeItem('userLastName');
    localStorage.removeItem('userPhone');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('companyName');
    localStorage.removeItem('telegramBotToken');
    localStorage.removeItem('telegramChatId');
    
    // Update authentication state
    setIsAuthenticated(false);
    setShowUserMenu(false);
    
    // Redirect to home page
    navigate('/');
  };

  const handleDropdownMouseEnter = (menu: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (menu !== 'services') setIsServicesOpen(false);
    if (menu !== 'prices') setIsPricesOpen(false);
    if (menu !== 'profile') setIsProfileOpen(false);

    if (menu === 'services') setIsServicesOpen(true);
    if (menu === 'prices') setIsPricesOpen(true);
    if (menu === 'profile') setIsProfileOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
      setIsPricesOpen(false);
      setIsProfileOpen(false);
    }, 150);
  };

  const handleContentMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return (
    <>
      <style>
        {`
          .nav-item {
            font-size: 12px;
            transition: color 0.2s ease;
          }
          
          .nav-item:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }

          .dropdown-item {
            font-size: 12px;
            transition: color 0.2s ease;
            padding: 8px 12px;
            border-radius: 6px;
          }

          .dropdown-item:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }
        `}
      </style>

      <nav className="fixed w-full z-50">
        <div className="bg-[#161617] relative z-50">
          <div className="max-w-[980px] mx-auto">
            <div className="flex justify-between items-center h-12">
              {/* Mobile Menu Button - Left */}
              <div className="md:hidden px-3">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="w-8 h-8 flex items-center justify-center text-[#f5f5f7] hover:text-white transition-colors rounded-full hover:bg-[#ffffff1a] active:bg-[#ffffff26]"
                >
                  <AlignRight className="h-4 w-4" />
                </button>
              </div>

              {/* Logo - Left on desktop, center on mobile */}
              <div className="hidden md:block px-3">
                <a href="/" className="flex items-center">
                  <img
                    src="https://i.ibb.co/t08SkQL/6069.png"
                    alt="6069 CARGO"
                    className="h-[20px]"
                    loading="eager"
                  />
                </a>
              </div>
              <div className="md:hidden absolute left-1/2 transform -translate-x-1/2">
                <a href="/" className="flex items-center">
                  <img 
                    src="https://i.ibb.co/t08SkQL/6069.png"
                    alt="6069 CARGO"
                    className="h-[20px]"
                    loading="eager"
                  />
                </a>
              </div>

              {/* Desktop Navigation - Hidden on Mobile */}
              <div className="hidden md:flex items-center justify-center flex-1">
                <div 
                  className="relative"
                  onMouseEnter={() => handleDropdownMouseEnter('services')}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <button className="nav-item text-[#f5f5f7] font-medium flex items-center px-3 py-2 rounded-full hover:bg-[#ffffff1a] transition-all">
                    {t('nav.services')} 
                    <ChevronDown className={`ml-1 h-3 w-3 transform transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                <a href="/prices" className="nav-item text-[#f5f5f7] font-medium px-3 py-2 rounded-full hover:bg-[#ffffff1a] transition-all">
                  {t('nav.prices')}
                </a>

                <button 
                  onClick={() => setShowRequestForm(true)}
                  className="nav-item text-[#f5f5f7] font-medium px-4 py-2 rounded-full bg-[#ffffff1a] hover:bg-[#ffffff26] transition-all flex items-center"
                >
                  <Package2 className="w-3 h-3 mr-1" />
                  {t('nav.order')}
                </button>
                <a href="/about" className="nav-item text-[#f5f5f7] font-medium px-3 py-2 rounded-full hover:bg-[#ffffff1a] transition-all">
                  {t('nav.about')}
                </a>
                <a href="/contacts" className="nav-item text-[#f5f5f7] font-medium px-3 py-2 rounded-full hover:bg-[#ffffff1a] transition-all">
                  {t('nav.contacts')}
                </a>
              </div>

              {/* User Icon - Right */}
              <div className="flex items-center px-3">
                <button
                  onClick={() => isAuthenticated ? setShowUserMenu(!showUserMenu) : handleProfileClick()}
                  className="w-8 h-8 flex items-center justify-center text-[#f5f5f7] hover:text-white transition-colors rounded-full hover:bg-[#ffffff1a] active:bg-[#ffffff26] mr-2"
                  title={isAuthenticated ? t('nav.dashboard') : t('nav.login')}
                >
                  <User className="h-4 w-4" />
                </button>

                {/* Language Switcher */}
                <div className="hidden md:block relative">
                  <button
                    onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                    className="flex items-center text-[#f5f5f7] hover:text-white px-2 py-1.5 rounded-full hover:bg-[#ffffff1a] transition-all text-xs"
                  >
                    <Globe className="w-3 h-3 mr-1" />
                    {language.toUpperCase()}
                    <ChevronDown className={`ml-0.5 h-3 w-3 transform transition-transform duration-300 ${showLanguageMenu ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showLanguageMenu && (
                    <div className="absolute right-0 mt-1 w-28 bg-[#1d1d1f] rounded-lg shadow-lg py-1 z-50 border border-[#424245]">
                      <button 
                        onClick={() => {
                          setLanguage('ru');
                          setShowLanguageMenu(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs ${language === 'ru' ? 'text-[#f5f5f7] font-medium' : 'text-[#86868b]'} hover:bg-[#ffffff1a]`}
                      >
                        Русский
                      </button>
                      <button 
                        onClick={() => {
                          setLanguage('en');
                          setShowLanguageMenu(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs ${language === 'en' ? 'text-[#f5f5f7] font-medium' : 'text-[#86868b]'} hover:bg-[#ffffff1a]`}
                      >
                        English
                      </button>
                      <button
                        onClick={() => {
                          setLanguage('zh');
                          setShowLanguageMenu(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs ${language === 'zh' ? 'text-[#f5f5f7] font-medium' : 'text-[#86868b]'} hover:bg-[#ffffff1a]`}
                      >
                        中文
                      </button>
                    </div>
                  )}
                </div>
                
                {/* User Menu Dropdown */}
                {isAuthenticated && showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                    <a
                      href="/client/dashboard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {t('nav.dashboard')}
                    </a>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Выйти
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dropdown Menus */}
        <div 
          className={`absolute w-full bg-[#1d1d1f] border-t border-[#424245] transition-all duration-300 ease-out ${
            isServicesOpen || isPricesOpen
              ? 'opacity-100 translate-y-0 visible'
              : 'opacity-0 -translate-y-1 invisible pointer-events-none'
          }`}
          onMouseEnter={handleContentMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          {isServicesOpen ? (
            <div className="max-w-[980px] mx-auto py-8 px-3">
              <div className="space-y-1">
                <a href="/services/business/search" className="dropdown-item block text-[#f5f5f7] hover:bg-[#ffffff1a] hover:text-white">
                  {t('services.search')}
                </a>
                <a href="/services/business/logistics" className="dropdown-item block text-[#f5f5f7] hover:bg-[#ffffff1a] hover:text-white">
                  {t('services.logistics')}
                </a>
                <a href="/services/business/forwarding" className="dropdown-item block text-[#f5f5f7] hover:bg-[#ffffff1a] hover:text-white">
                  {t('services.inspection')}
                </a>
                <a href="/services/business/payment" className="dropdown-item block text-[#f5f5f7] hover:bg-[#ffffff1a] hover:text-white">
                  {t('services.payment')}
                </a>
              </div>
            </div> 
          ) : null}
        </div>
      </nav>

      <MobileMenu 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)}
        isAuthenticated={isAuthenticated}
        language={language}
        setLanguage={setLanguage}
        onLogout={() => {}}
      />

      {/* Request Form Popup */}
      {showRequestForm && (
        <RequestForm 
          isPopup 
          source={language === 'ru' ? "Главное меню" : "Main Menu"}
          onClose={() => setShowRequestForm(false)} 
        />
      )}
    </>
  );
}

export default NavigationMenu;